import { Loader2, Mail } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { isRestrictedEmail } from '@/shared/constants/restrictedEmails'
import { toast } from '@/shared/lib/toast'

import { useSalesLeadsStore } from '../../stores/salesLeads.store'

export default function EmailModal({ open, onOpenChange, lead }) {
  const [isSending, setIsSending] = useState(false)

  const sendingRef = useRef(false)

  const productName = lead?.products?.[0]?.product || 'your requirement'

  const [form, setForm] = useState(() => ({
    to: lead?.email ?? '',
    subject: lead ? `Following up — ${lead.company}` : '',
    message: lead
      ? `Hi ${lead.name},

Following up on our conversation about ${productName}.

Regards`
      : '',
  }))

  const sendLeadEmail = useSalesLeadsStore((state) => state.sendLeadEmail)

  const isRestricted = isRestrictedEmail(form.to || lead?.email)

  const handleChange = (field, value) => {
    if (sendingRef.current) return

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSend = async () => {
    if (!lead || sendingRef.current) {
      return
    }

    if (isRestricted) {
      toast.error('Sending email to this address is restricted')
      return
    }

    sendingRef.current = true
    setIsSending(true)

    try {
      console.log('Starting send email API call...')

      const result = await sendLeadEmail(lead._id, {
        subject: form.subject,
        message: form.message,
      })

      console.log('Send email API completed:', result)

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to send email:', error)
    } finally {
      sendingRef.current = false
      setIsSending(false)

      console.log('Email sending finished')
    }
  }

  const handleClose = (nextOpen) => {
    if (sendingRef.current) {
      return
    }

    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="
          w-[calc(100%-16px)]
          max-w-3xl
          rounded-3xl
          p-4
          sm:w-full
          sm:p-6
        "
      >
        {/* Header */}
        <DialogHeader>
          <DialogTitle
            className="
              flex
              items-start
              gap-2
              text-center
              text-2xl
              font-semibold
              leading-tight
              sm:text-left
              sm:text-3xl
            "
          >
            <Mail className="mt-1 size-5 shrink-0 sm:size-6" />

            <span className="min-w-0 break-words">Email {lead?.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-5">
          {/* To */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-muted-foreground">
              To
            </label>

            <Input
              value={form.to}
              readOnly
              className="h-12 w-full rounded-full"
            />
            {isRestricted && (
              <p className="mt-1.5 text-xs font-medium text-rose-500">
                Sending email to this address is restricted.
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-muted-foreground">
              Subject
            </label>

            <Input
              value={form.subject}
              disabled={isSending}
              onChange={(event) => handleChange('subject', event.target.value)}
              className="h-12 w-full rounded-full"
            />
          </div>

          {/* Message */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-muted-foreground">
              Message
            </label>

            <textarea
              rows={7}
              value={form.message}
              disabled={isSending}
              onChange={(event) => handleChange('message', event.target.value)}
              className="
                min-h-[180px]
                w-full
                resize-none
                rounded-2xl
                border
                bg-transparent
                p-3
                text-sm
                outline-none
                transition
                focus:ring-2
                disabled:cursor-not-allowed
                disabled:opacity-60
                sm:min-h-[200px]
              "
            />
          </div>

          {/* Actions */}
          <div
            className="
              flex
              flex-col-reverse
              gap-2
              sm:flex-row
              sm:justify-end
              sm:gap-3
            "
          >
            <Button
              type="button"
              variant="outline"
              disabled={isSending}
              onClick={() => handleClose(false)}
              className="w-full rounded-full sm:w-auto"
            >
              Cancel
            </Button>

            <Button
              type="button"
              disabled={isSending || isRestricted}
              onClick={handleSend}
              className="
                w-full
                min-w-[110px]
                rounded-full
                bg-[#0F172A]
                text-white
                hover:bg-slate-700
                disabled:cursor-not-allowed
                disabled:opacity-60
                sm:w-auto
              "
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 size-4" />
                  Send
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
